// The open article's discussion: the last section of the article, filled in once the article has been
// open a moment, for the sources whose discussions can be read (core discussionProvider). Everything
// it draws is core's shared view (discussionView.ts); this only decides when to ask, where the answer
// goes, and how the rest of the reader reaches it (the byline's count, Contents, the C key).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import { jumpTo, mountDiscussion, hasDiscussion, discussionLink, discussionProvider, discussionReader, type DiscussionInfo, type DiscussionView, type Section } from "@vladrss/core";
import { api, useOwnerAction, useOwnerQuery } from "../lib/api";
import { discussions } from "../lib/discussions";
import { toast, type Row } from "../lib/store";

/** How long the article stays open before its discussion is asked for. */
export const DWELL = 1000;

/** The discussion as a section of the outline: it opens in place when the outline jumps to it. */
export type Entry = Section & { open?: () => void };

export function useDiscussion(row: Row, scroller: RefObject<HTMLElement | null>) {
  const open = useOwnerAction(api.discussion.read);
  const policy = useOwnerQuery(api.feeds.status, {});
  const hostRef = useRef<HTMLElement | null>(null);
  const view = useRef<DiscussionView | null>(null);
  const wanted = useRef(false); // C pressed before the discussion was there
  const [seen, setSeen] = useState<{ id: string; info: DiscussionInfo } | null>(null);
  /** Whether this article has a discussion that can be read at all: only then is there a section. */
  const has = hasDiscussion(row.url, row.kind) && policy?.discussionsEnabled !== false;
  const phone = has && discussionReader(discussionProvider(row.url) ?? "") === "phone";
  const copies = useOwnerQuery(api.discussion.copies, phone ? {} : "skip");
  const revision = copies?.find(copy => copy.articleId === row.id)?.revision ?? null;
  const info = seen?.id === row.id ? seen.info : undefined;

  const go = useCallback(() => {
    const sc = scroller.current, section = hostRef.current;
    if (!sc || !section || section.hidden) return;
    view.current?.expand();
    const far = Math.abs(section.getBoundingClientRect().top - sc.getBoundingClientRect().top) > sc.clientHeight;
    const from = jumpTo(sc, section);
    // a jump of more than a screen offers the way back, as the outline's does
    if (far) toast("Jumped to Discussion", () => jumpTo(sc, from), "comment", "Back");
  }, [scroller]);
  const goRef = useRef(go);
  goRef.current = go;

  useLayoutEffect(() => {
    const section = hostRef.current;
    if (!has || !section) return;
    const mounted = mountDiscussion(section, {
      link: discussionLink(row.url),
      load: (cursor, fresh) => discussions.load(row.id, cursor, () => open({ id: row.id as never, protocol: 3, ...(cursor ? { cursor } : {}) }), fresh),
    }, (i) => {
      setSeen({ id: row.id, info: i });
      if (wanted.current && i.phase === "ready") { wanted.current = false; if (i.state !== "none") requestAnimationFrame(() => goRef.current()); }
    });
    view.current = mounted;
    const timer = setTimeout(() => mounted.start(), DWELL);
    return () => { clearTimeout(timer); mounted.dispose(); view.current = null; wanted.current = false; };
  }, [row.id, row.url, has, open]);

  useEffect(() => {
    if (!phone || copies === undefined) return;
    if (discussions.revise(row.id, revision) && view.current?.info().phase !== "idle") view.current?.refresh();
  }, [phone, row.id, revision, copies]);

  /** C: to the discussion, asking for it now if it has not been asked for yet. */
  const jump = useCallback(() => {
    const i = view.current?.info();
    if (!view.current || !i) return;
    if (i.phase === "idle") { wanted.current = true; view.current.start(); return; }
    if (i.phase === "loading") { wanted.current = true; return; }
    if (i.state !== "none") go();
  }, [go]);
  useEffect(() => {
    if (!has) return;
    addEventListener("vladrss:discussion", jump);
    return () => removeEventListener("vladrss:discussion", jump);
  }, [has, jump]);

  // the outline lists Discussion last, once there is one to open
  const shown = info?.phase === "ready" && info.state === "ok";
  const entry = useMemo<Entry | null>(
    () => (shown && hostRef.current ? { el: hostRef.current, label: "Discussion", level: 1, open: () => view.current?.expand() } : null),
    [shown, row.id],
  );
  /** The count for the byline, once a discussion with comments is known. */
  const count = shown ? info.total : undefined;
  return { has, hostRef, count, entry, jump };
}
